import { PaginationT } from 'models/Pagination'
import { Fields as UserModel } from 'models/User'
import { Fields as UserProfileFields } from 'models/UserProfile'
import { api } from './index'

export type User = {
  id: number
  uid: string
  email: string
  first_name: string
  last_name: string
}

export type GiftCard = {
  id: number
  last4: string
  provider: string
  created_at: string
  state: 'active' | 'inactive' | 'archived'
}

type ProgressPayload = {
  grant_type: 'progress'
  grant_progress: {
    grant_progress_type: 'dollar' | 'percentage'
    grant_value: number
  }
  rating_id: number
}

type RewardPayload = {
  grant_type: 'reward'
  grant_reward: {
    program_id: number
    issuance_reason?: string
  }
  rating_id: number
}

export type GrantPayload = ProgressPayload | RewardPayload

export type GrantParams = {
  user_uid: string
  grant_payload: GrantPayload
}

type ModifyPointsParams = {
  user_uid: string
  amount: number
}

type GrantUserTierParams = {
  userUid: string
  state: string
}

type GrantPurchaseParams = {
  userUid: string
  purchase: {
    purchased_at: string
    amount: number
    location_id: number
  }
}

type GetUsersParams = {
  page?: number
  per_page?: number
  query?: string
}

type GetUsersResponse = {
  users: UserModel[]
  pagination: PaginationT
  total_membership_count: number
}

export const usersApi = api.injectEndpoints({
  endpoints: builder => ({
    getUsers: builder.query<GetUsersResponse, GetUsersParams>({
      query: ({ page = 1, per_page, query }) => ({
        url: 'users',
        method: 'GET',
        params: { page, per_page, query },
      }),
      providesTags: ['User'],
    }),
    // GET users/:identifier — the same payload actions/users#getUserProfile
    // fetches and upserts into the UserProfile model, so it is typed with that
    // model's Fields (the narrow local `User` type above under-described it:
    // the response also carries tier, lifetime spend/purchases, phone, …).
    // Keyed by users.identifier (uid) or a hashid — the backend's
    // GET /users/:identifier does NOT resolve raw numeric ids, so callers must
    // pass the uid string (RatingDetail is currently the only consumer).
    getUser: builder.query<{ profile: UserProfileFields }, string>({
      query: userId => `users/${userId}`,
      providesTags: (_result, _error, userId) => [{ type: 'User', id: userId }],
    }),
    getUserGiftCards: builder.query<
      {
        gift_cards: GiftCard[]
        pagination: PaginationT
      },
      { userUid: string; page?: number }
    >({
      query: ({ userUid, page = 1 }) => ({
        url: `users/${userUid}/gift_cards`,
        params: { page },
      }),
      providesTags: (_result, _error, { userUid, page }) => [
        { type: 'UserGiftCard', id: `${userUid}-${page}` },
      ],
    }),
    grantReward: builder.mutation<any, GrantParams>({
      query: grant_params => ({
        url: `users/${grant_params.user_uid}/grant_reward`,
        method: 'POST',
        body: grant_params.grant_payload,
      }),
      invalidatesTags: ['Feedback'],
    }),
    grantPoints: builder.mutation<any, ModifyPointsParams>({
      query: ({ amount, user_uid }) => ({
        url: `users/${user_uid}/grant_points`,
        method: 'POST',
        body: {
          amount: amount,
        },
      }),
    }),
    block: builder.mutation<any, string>({
      query: userUid => ({
        url: `users/${userUid}/deactivate`,
        method: 'POST',
      }),
    }),
    grantUserTier: builder.mutation<any, GrantUserTierParams>({
      query: ({ userUid, state }) => ({
        url: `users/${userUid}/grant_user_tier`,
        method: 'POST',
        body: {
          state: state,
        },
      }),
    }),
    grantPurchase: builder.mutation<any, GrantPurchaseParams>({
      query: ({ userUid, purchase }) => ({
        url: `users/${userUid}/grant_purchase`,
        method: 'POST',
        body: { provider_purchase: purchase },
      }),
    }),
    clawbackPoints: builder.mutation<any, ModifyPointsParams>({
      query: ({ amount, user_uid }) => ({
        url: `users/${user_uid}/clawback_points`,
        method: 'POST',
        body: {
          amount: amount,
        },
      }),
    }),
  }),
})

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useGetUserGiftCardsQuery,
  useGrantRewardMutation,
  useGrantPointsMutation,
  useBlockMutation,
  useGrantUserTierMutation,
  useGrantPurchaseMutation,
  useClawbackPointsMutation,
} = usersApi
